import React, { useMemo, useState } from "react";
import { Box, Typography, Chip, IconButton, Dialog, DialogTitle, DialogContent } from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseIcon from "@mui/icons-material/Close";
import { exportToExcel, ExcelData } from "../../../utils/excelExport";
import styles from "./buyerPRList.module.scss";

interface BuyerPRListProps {
  title?: string;
  data?: any[];
  buyerKey?: string;
  completedKey?: string;
  totalKey?: string;
  showThisYear?: boolean;
  itemsPerPage?: number;
  onDownload?: () => void;
}

const BuyerPRList: React.FC<BuyerPRListProps> = ({
  title = "PR by Buyers",
  data = [],
  buyerKey = "buyer",
  completedKey = "completed",
  totalKey = "total",
  showThisYear = false,
  itemsPerPage = 5,
  onDownload,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const defaultColor = "#00d2aa";

  const handleExpandClick = () => {
    setIsExpanded(true);
  };

  const handleCloseExpand = () => {
    setIsExpanded(false);
  };

  const handleDownload = () => {
    if (onDownload) {
      onDownload();
      return;
    }

    const excelData: ExcelData = {
      sheetName: title,
      fileName: title.replace(/\s+/g, "_"),
      columns: [
        { header: "Buyer Name", key: "buyer", width: 30 },
        { header: "Completed PRs", key: "completed", width: 15 },
        { header: "Total PRs", key: "total", width: 15 },
        { header: "Completion %", key: "completionPercentage", width: 15 },
      ],
      data: chartData.map((item) => ({
        ...item,
        completionPercentage: item.completionPercentage.toFixed(2),
      })),
    };

    exportToExcel(excelData);
  };

  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];

    return data.map((item, index) => {
      const completed = Number(item[completedKey] || 0);
      const total = Number(item[totalKey] || 0);
      return {
        buyer: item[buyerKey] || `Buyer ${index + 1}`,
        completed,
        total,
        completionPercentage: total > 0 ? (completed / total) * 100 : 0,
      };
    });
  }, [data, buyerKey, completedKey, totalKey]);

  const displayData = useMemo(() => {
    return chartData.slice(0, itemsPerPage);
  }, [chartData, itemsPerPage]);

  if (!chartData || chartData.length === 0) {
    return (
      <Box className={styles.container}>
        <Box className={styles.header}>
          <Typography variant="h6" className={styles.title}>
            {title}
          </Typography>
          {showThisYear && (
            <Chip
              label="This Year"
              size="small"
              sx={{
                backgroundColor: "#e6faf5",
                color: "#00d2aa",
                fontWeight: 600,
                fontSize: "0.75rem",
              }}
            />
          )}
        </Box>
        <Box
          className={styles.listContainer}
          display="flex"
          alignItems="center"
          justifyContent="center"
          minHeight={200}
        >
          <Typography variant="body2" color="textSecondary">
            No data available
          </Typography>
        </Box>
      </Box>
    );
  }

  const renderBuyerList = (dataToRender: typeof chartData, isModal = false) => (
    <Box className={isModal ? styles.modalListContainer : styles.listContainer}>
      {dataToRender.map((item, index) => (
        <Box key={index} className={styles.listItem}>
          <Typography className={styles.buyerName}>{item.buyer}</Typography>
          <Box className={styles.barSection}>
            <Box className={styles.barBackground}>
              <Box
                className={styles.barFill}
                style={{
                  width: `${item.completionPercentage}%`,
                  background: defaultColor,
                }}
              />
            </Box>
          </Box>
          <Typography className={styles.ratio}>
            {item.completed}/{item.total}
          </Typography>
        </Box>
      ))}
    </Box>
  );

  return (
    <>
      <Box className={styles.container}>
        <Box className={styles.header}>
          <Typography variant="h6" className={styles.title}>
            {title}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {showThisYear && (
              <Chip
                label="This Year"
                size="small"
                sx={{
                  backgroundColor: "#e6faf5",
                  color: "#00d2aa",
                  fontWeight: 500,
                  fontSize: "0.75rem",
                }}
              />
            )}
            {chartData.length > itemsPerPage && (
              <IconButton
                onClick={handleExpandClick}
                size="small"
                sx={{
                  color: "#5b6b9e",
                  "&:hover": {
                    backgroundColor: "rgba(91, 107, 158, 0.1)",
                  },
                }}
                aria-label="expand"
              >
                <OpenInFullIcon fontSize="small" />
              </IconButton>
            )}
            <IconButton
              onClick={handleDownload}
              size="small"
              sx={{
                color: "#5b6b9e",
                "&:hover": {
                  backgroundColor: "rgba(91, 107, 158, 0.1)",
                },
              }}
              aria-label="download"
            >
              <FileDownloadOutlinedIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {renderBuyerList(displayData)}
      </Box>

      <Dialog
        open={isExpanded}
        onClose={handleCloseExpand}
        maxWidth={false}
        fullScreen
        className={styles.expandDialog}
      >
        <DialogTitle className={styles.dialogTitle}>
          <Box className={styles.dialogTitleLeft}>
            <Typography variant="h6" className={styles.dialogTitleText}>
              {title}
            </Typography>
            {showThisYear && (
              <Chip
                label="This Year"
                size="small"
                sx={{
                  backgroundColor: "#e6faf5",
                  color: "#00d2aa",
                  fontWeight: 500,
                  fontSize: "0.75rem",
                  marginLeft: 2,
                }}
              />
            )}
          </Box>
          <Box className={styles.dialogActions}>
            <IconButton
              onClick={handleDownload}
              size="small"
              sx={{
                color: "#5b6b9e",
                "&:hover": {
                  backgroundColor: "rgba(91, 107, 158, 0.1)",
                },
              }}
              aria-label="download"
            >
              <FileDownloadOutlinedIcon fontSize="small" />
            </IconButton>
            <IconButton
              aria-label="close"
              onClick={handleCloseExpand}
              sx={{
                color: "#5b6b9e",
                "&:hover": {
                  backgroundColor: "rgba(91, 107, 158, 0.1)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent className={styles.dialogContent}>
          {renderBuyerList(chartData, true)}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BuyerPRList;
